import { useCallback, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { OnboardingFormFieldValues } from './useOnboardingFormContext';

interface UseSuggestedWebsiteArgs {
  formContext: UseFormReturn<OnboardingFormFieldValues>;
  suggestedWebsite?: string;
  isWebsiteVerified: boolean;
  pickedCompanyKey?: string;
}

interface UseSuggestedWebsiteReturnValues {
  suggestedWebsite?: string;
  isWebsiteVerified: boolean;
  isWebsiteEdited: boolean;
  shouldShowWebsitePreview: boolean;
  shouldShowWebsiteField: boolean;
  startEditingWebsite: () => void;
}

export function useSuggestedWebsite({
  formContext,
  suggestedWebsite,
  isWebsiteVerified,
  pickedCompanyKey,
}: UseSuggestedWebsiteArgs): UseSuggestedWebsiteReturnValues {
  const { setValue, getValues, formState } = formContext;
  const [isWebsiteEdited, setIsWebsiteEdited] = useState(false);
  const hasWebsiteError = !!formState.errors.website;

  useEffect(() => {
    setIsWebsiteEdited(false);
  }, [pickedCompanyKey]);

  useEffect(() => {
    if (hasWebsiteError) {
      setIsWebsiteEdited(true);
    }
  }, [hasWebsiteError]);

  useEffect(() => {
    if (isWebsiteEdited) {
      return;
    }

    setValue('website', suggestedWebsite ?? '', { shouldValidate: false });
  }, [isWebsiteEdited, setValue, suggestedWebsite]);

  const startEditingWebsite = useCallback(() => {
    setValue('website', suggestedWebsite ?? getValues('website') ?? '', {
      shouldValidate: false,
    });
    setIsWebsiteEdited(true);
  }, [getValues, setValue, suggestedWebsite]);

  const hasSuggestedWebsite = !!suggestedWebsite;

  return {
    suggestedWebsite,
    isWebsiteVerified,
    isWebsiteEdited,
    shouldShowWebsitePreview: hasSuggestedWebsite && !isWebsiteEdited,
    shouldShowWebsiteField: !hasSuggestedWebsite || isWebsiteEdited,
    startEditingWebsite,
  };
}
